// Re-export of the shared domain types for SPA imports (`../lib/types.js`
// instead of reaching into `@contentgen/shared` from every page).
import type {
  ApiTokenRecord,
  ContentType,
  Deliverable,
  DeliverableStatus,
  Export,
  Job,
  Output,
  StatsRow,
} from "@contentgen/shared";

export type {
  ApiTokenRecord,
  ContentType,
  Deliverable,
  DeliverableDependency,
  DeliverableStatus,
  DeliverableSummary,
  Executor,
  Export,
  ExportStatus,
  Item,
  Job,
  JobError,
  JobStatus,
  JobUsage,
  ModelRoute,
  Output,
  Project,
  ProjectDefault,
  PromptTemplate,
  ProviderId,
  Rating,
  ReferenceAsset,
  RouteCapabilities,
  RoutePricing,
  StatsRow,
  TokenScope,
} from "@contentgen/shared";

/** An Output as `GET /api/deliverables/:id` serializes it: the stored row plus
 *  a fresh presigned GET url and the `<jobId>:<variantIndex>` opaque id. */
export type OutputWithUrl = Output & { outputId: string; url: string };

/** The full `GET /api/deliverables/:id` payload: the stored deliverable plus its
 *  job history and presigned outputs. Shared by the item page and the review
 *  queue so neither drifts from the other. */
export interface DeliverableDetail {
  deliverable: Deliverable;
  jobs: Job[];
  outputs: OutputWithUrl[];
}

/** Job statuses that mean "still working" - a deliverable with any job in one of
 *  these is polled for live updates (sizing table: POLL_INTERVAL_MS). */
export const NON_TERMINAL_JOB_STATUSES = ["queued", "claimed", "running"] as const;

/** A row of `GET /api/stats`: the raw counters plus the server-computed score. */
export type StatsRowWithScore = StatsRow & { score: number };

/** `POST /api/tokens` echoes the new record plus the plaintext token once. */
export type MintedApiToken = ApiTokenRecord & { token: string };

/** An Export row as `GET /api/exports/:id` serializes it: the stored row plus
 *  a fresh presigned download URL once `status` is `ready`. */
export type ExportWithUrl = Export & { url?: string };

/** One page of `GET /api/projects/:id/book` (Picture-Book Workflow plan). A page
 *  with no accepted illustration still appears, without an `illustration`. */
export interface BookPage {
  pageNumber: number;
  itemId: string;
  title: string;
  pageText: string;
  status: DeliverableStatus;
  illustration?: {
    outputId: string;
    url: string;
    mimeType: string;
    width?: number;
    height?: number;
  };
}

/** The whole `GET /api/projects/:id/book` payload: the book title and its pages
 *  in ascending page order. */
export interface BookResponse {
  title: string;
  pages: BookPage[];
}

/** One route's slice of a bulk-generate estimate
 *  (`POST /api/projects/:id/generate-estimate`). */
export interface EstimateRouteRow {
  routeId: string;
  contentType: ContentType;
  deliverables: number;
  variants: number;
  usd: number;
}

/** The whole `generate-estimate` response: a projected cost for a set of
 *  deliverables before anything is queued (G-5). `unpriced` counts
 *  deliverables with no price; `unresolved` says why, one entry each. */
export interface EstimateResponse {
  count: number;
  totalUsd: number;
  unpriced: number;
  byRoute: EstimateRouteRow[];
  unresolved?: Array<{ deliverableId: string; reason: string }>;
}

/** `GET /api/jobs?status=&day=&cursor=`: one GSI3 day partition of job history,
 *  newest first, `status` already filtered server-side. */
export interface JobsResponse {
  items: Job[];
  nextCursor?: string;
  day: string;
}

/** `GET /api/dashboard`: the Dashboard control panel in one round trip,
 *  composed server-side so the SPA holds no rules. */
export interface DashboardSummary {
  deliverables: { pending: number; generating: number; review: number; accepted: number; rejected: number };
  jobs: { queued: number; running: number; failedLast24h: number; succeededLast24h: number };
  spend: {
    month: string;
    totalUsd: number;
    byRoute: Array<{ routeId: string; contentType: ContentType; usd: number; jobs: number }>;
  };
  routes: { enabled: number; total: number; needsVerification: number };
  workers: Array<{
    workerId: string;
    hostname: string;
    lastSeenAt: string;
    currentJobId?: string;
    routeIds: string[];
  }>;
  setup: Array<{ id: string; label: string; done: boolean; hint: string }>;
}
